import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@beyou/environments/environment';
import { firstValueFrom, map, Observable } from 'rxjs';
import { UserDetailsDto } from '../../dto/users/user.dto';
import { SubriseUserDetails } from '../../models/users/subrise-user.model';
import { UserMapper } from '../../mappers/users/user.mapper';
import { toDateString } from '../../utils/datehelper';

export type LanguagePreferencesUpdateDto = {
  language_id?: number;
  ui_language?: string;
};

export type AppUserMeUpdateDto = {
  first_name: string;
  email: string;
  last_name?: string | null;
  gender?: string | null;
  language_id?: number | null;
  birthday?: string | null;
  favorite_place?: string | null;
  anchoring_action?: string | null;
  anchoring_word?: string | null;
  self_hypnosis_action?: string | null;
  first_name_phonetic_spelling?: string | null;
};

/**
 * The signed-in partner's own user on the Subrise app API, which is a different host and a
 * different payload shape from the back-office `/users` endpoints `UserApiService` covers — the
 * app's `/users/me` carries no roles, sessions or subscriptions, so they are filled in here
 * before the shared `UserMapper` runs.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerUserApiService {

  constructor(private readonly http: HttpClient) { }

  /** Creates or refreshes the local user behind the Firebase identity; `/users/me` 404s without it. */
  public syncPartnerFirebaseLocalUser(): Observable<void> {
    const apiUrl = `${this.getAppApiBase()}/users/firebase-user-sync`;
    return this.http.post<void>(apiUrl, {}).pipe(map(() => undefined));
  }

  public async getPartnerMe(): Promise<SubriseUserDetails> {
    const apiUrl = `${this.getAppApiBase()}/users/me`;
    return await firstValueFrom(this.http.get<any>(apiUrl).pipe(
      map((response) => {
        const adapted: UserDetailsDto = {
          ...response,
          roles: [],
          session_ids: [],
          subscriptions: [],
          smart_user_program_ids: [],
        };
        return UserMapper.toDetailedModel(adapted);
      })
    ));
  }

  public async updatePartnerMe(user: SubriseUserDetails): Promise<void> {
    const apiUrl = `${this.getAppApiBase()}/users/me`;
    const body: AppUserMeUpdateDto = {
      first_name: user.firstName,
      email: user.email,
      last_name: user.lastName || null,
      gender: user.gender || null,
      language_id: user.languageId,
      birthday: user.birthday ? toDateString(user.birthday) : null,
      favorite_place: user.favoritePlace || null,
      anchoring_action: user.anchoringAction || null,
      anchoring_word: user.anchoringWord || null,
      self_hypnosis_action: user.selfHypnosisAction || null,
      first_name_phonetic_spelling: user.firstNamePhoneticSpelling || null,
    };
    return await firstValueFrom(
      this.http.put(apiUrl, body, { observe: 'response' }).pipe(
        map((response) => {
          if (response.status === 204) {
            return;
          }
          throw new Error(`Unexpected response status: ${response.status}`);
        })
      )
    );
  }

  /**
   * The two language dropdowns in Settings. Not partner-specific despite this service's name: the
   * app API's `/users/me` answers for every signed-in user, and this is the only write that does
   * not need the back-office `users/{id}` permission. Omitted fields are left untouched.
   */
  public updateLanguagePreferences(preferences: LanguagePreferencesUpdateDto): Observable<void> {
    const apiUrl = `${this.getAppApiBase()}/users/me/language-preferences`;
    return this.http.put(apiUrl, preferences).pipe(map(() => undefined));
  }

  /** The browser's time zone, so dates in the emails we send match the ones in the portal. */
  public updateTimeZone(timeZone: string): Observable<void> {
    const apiUrl = `${this.getAppApiBase()}/users/me/time-zone`;
    return this.http.put(apiUrl, { time_zone: timeZone }).pipe(map(() => undefined));
  }

  private getAppApiBase(): string {
    const base = environment.subriseApiBaseUrl?.trim() ?? '';
    if (!base) {
      throw new Error('subriseApiBaseUrl is not set');
    }
    return base.replace(/\/$/, '');
  }
}
